import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import BusinessLink from "@/components/BusinessLink";
import { motion } from "framer-motion";
import { Search, MapPin, Star, ArrowRight, Building2, Users, FileText, Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Header = () => {
  const { user, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <header className="glass sticky top-0 z-50 border-b border-slate-200/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <MapPin className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-xl tracking-tight">lokalnie.pro</span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-6">
            <Link to="/szukaj" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-search">
              Katalog firm
            </Link>
            {user ? (
              <>
                <Link to="/dashboard" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-dashboard">
                  Panel
                </Link>
                <Button variant="ghost" onClick={logout} data-testid="logout-btn">
                  Wyloguj
                </Button>
              </>
            ) : (
              <>
                <Link to="/login" data-testid="nav-login">
                  <Button variant="ghost">Zaloguj</Button>
                </Link>
                <Link to="/rejestracja" data-testid="nav-register">
                  <Button className="bg-amber-500 hover:bg-amber-600 text-white">
                    Rozpocznij
                  </Button>
                </Link>
              </>
            )}
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="md:hidden py-4 border-t border-slate-200"
          >
            <nav className="flex flex-col gap-3">
              <Link to="/szukaj" className="text-sm font-medium py-2" onClick={() => setMobileMenuOpen(false)}>
                Katalog firm
              </Link>
              {user ? (
                <>
                  <Link to="/dashboard" className="text-sm font-medium py-2" onClick={() => setMobileMenuOpen(false)}>
                    Panel
                  </Link>
                  <Button variant="ghost" onClick={() => { logout(); setMobileMenuOpen(false); }}>
                    Wyloguj
                  </Button>
                </>
              ) : (
                <>
                  <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="ghost" className="w-full">Zaloguj</Button>
                  </Link>
                  <Link to="/rejestracja" onClick={() => setMobileMenuOpen(false)}>
                    <Button className="w-full bg-amber-500 hover:bg-amber-600 text-white">
                      Rozpocznij
                    </Button>
                  </Link>
                </>
              )}
            </nav>
          </motion.div>
        )}
      </div>
    </header>
  );
};

const StarRating = ({ rating }) => {
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`w-4 h-4 ${star <= rating ? 'fill-amber-400 text-amber-400' : 'text-slate-300'}`}
        />
      ))}
    </div>
  );
};

export default function HomePage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [stats, setStats] = useState({ total_businesses: 0, total_landing_pages: 0, categories_count: 0 });
  const [featuredBusinesses, setFeaturedBusinesses] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, businessesRes, categoriesRes] = await Promise.all([
          axios.get(`${API}/stats`),
          axios.get(`${API}/businesses?limit=6`),
          axios.get(`${API}/businesses/categories`)
        ]);
        
        setStats(statsRes.data);
        // /businesses returns {items, total, ...} (paginated). Fallback to plain array.
        setFeaturedBusinesses(businessesRes.data.items || businessesRes.data);
        setCategories(categoriesRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    
    fetchData();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    navigate(`/szukaj?query=${encodeURIComponent(searchQuery)}`);
  };

  return (
    <div className="min-h-screen">
      <Header />

      {/* Hero Section */}
      <section className="hero-gradient py-16 sm:py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-7"
            >
              <span className="inline-block text-xs font-semibold tracking-widest text-amber-600 uppercase mb-4">
                Katalog firm lokalnych
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-none mb-6">
                Twoja firma,<br />
                <span className="text-amber-500">widoczna online</span>
              </h1>
              <p className="text-lg text-slate-600 mb-8 max-w-xl leading-relaxed">
                Znajdź lokalne firmy lub stwórz profesjonalną stronę dla swojego biznesu. 
                AI wygeneruje atrakcyjną treść na podstawie Twojej wizytówki Google.
              </p>

              {/* Search Form */}
              <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3 max-w-lg">
                <div className="relative flex-1">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    type="text"
                    placeholder="Szukaj firmy, usługi..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-12 h-12 text-base"
                    data-testid="hero-search-input"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="h-12 px-6 bg-primary hover:bg-primary/90"
                  data-testid="hero-search-btn"
                >
                  Szukaj
                </Button>
              </form>

              {/* Quick Categories */}
              <div className="mt-6 flex flex-wrap gap-2">
                {categories.slice(0, 5).map((cat) => (
                  <button
                    key={cat}
                    onClick={() => navigate(`/szukaj?category=${encodeURIComponent(cat)}`)}
                    className="category-badge hover:bg-slate-200 cursor-pointer"
                    data-testid={`category-${cat}`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </motion.div>

            {/* Right Stats */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="lg:col-span-5"
            >
              <div className="grid grid-cols-2 gap-4">
                <Card className="card-hover">
                  <CardContent className="p-6 text-center">
                    <Building2 className="w-8 h-8 text-amber-500 mx-auto mb-3" />
                    <p className="text-3xl font-bold text-slate-900">{stats.total_businesses}</p>
                    <p className="text-sm text-slate-500 mt-1">Firm w katalogu</p>
                  </CardContent>
                </Card>
                <Card className="card-hover">
                  <CardContent className="p-6 text-center">
                    <FileText className="w-8 h-8 text-emerald-500 mx-auto mb-3" />
                    <p className="text-3xl font-bold text-slate-900">{stats.total_landing_pages}</p>
                    <p className="text-sm text-slate-500 mt-1">Landing pages</p>
                  </CardContent>
                </Card>
                <Card className="card-hover col-span-2">
                  <CardContent className="p-6 text-center">
                    <Users className="w-8 h-8 text-blue-500 mx-auto mb-3" />
                    <p className="text-3xl font-bold text-slate-900">{stats.categories_count}</p>
                    <p className="text-sm text-slate-500 mt-1">Kategorii działalności</p>
                  </CardContent>
                </Card>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Featured Businesses */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-10">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
                Polecane firmy
              </h2>
              <p className="text-slate-500 mt-2">Odkryj najlepiej oceniane lokalne biznesy</p>
            </div>
            <Link to="/szukaj" data-testid="see-all-link">
              <Button variant="ghost" className="group">
                Zobacz wszystkie
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {featuredBusinesses.map((business, index) => (
              <motion.div
                key={business.business_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                <BusinessLink business={business} data-testid={`business-card-${business.business_id}`}>
                  <Card className="card-hover overflow-hidden group">
                    <div className="aspect-video relative overflow-hidden">
                      <img
                        src={business.photos?.[0] || 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800'}
                        alt={business.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-3 left-3">
                        <Badge className="bg-white/90 text-slate-700 backdrop-blur-sm">
                          {business.category}
                        </Badge>
                      </div>
                      {!business.website && (
                        <div className="absolute top-3 right-3">
                          <Badge className="bg-amber-500 text-white">
                            Potrzebuje strony
                          </Badge>
                        </div>
                      )}
                    </div>
                    <CardContent className="p-5">
                      <h3 className="font-semibold text-lg text-slate-900 mb-2 group-hover:text-amber-600 transition-colors">
                        {business.name}
                      </h3>
                      <div className="flex items-center gap-2 text-sm text-slate-500 mb-3">
                        <MapPin className="w-4 h-4" />
                        <span>{business.city}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <StarRating rating={Math.round(business.rating)} />
                          <span className="text-sm font-medium text-slate-700">{business.rating}</span>
                        </div>
                        <span className="text-xs text-slate-400">{business.reviews_count} opinii</span>
                      </div>
                    </CardContent>
                  </Card>
                </BusinessLink>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-24 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              Twój biznes zasługuje na stronę internetową
            </h2>
            <p className="text-slate-300 text-lg mb-8 max-w-2xl mx-auto">
              Nie masz strony WWW? AI wygeneruje profesjonalny landing page 
              na podstawie Twojej wizytówki Google Maps - w kilka sekund!
            </p>
            <Link to="/rejestracja" data-testid="cta-register-btn">
              <Button size="lg" className="bg-amber-500 hover:bg-amber-600 text-white px-8 h-12">
                Stwórz stronę za darmo
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-50 py-12 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <MapPin className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-lg">lokalnie.pro</span>
            </div>
            <p className="text-sm text-slate-500">
              © 2024 lokalnie.pro. Wszystkie prawa zastrzeżone.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
